import React, { useEffect, useState } from "react";
import { Button, FloatButton, Layout, ConfigProvider, Tour } from "antd";
import { Navigate, useNavigate } from "react-router-dom";
import SideNav from "../components/SideNav/SideNav";
import AppHeader from "../components/Header/Header";
import { FolderOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../hooks/reduxHooks";
import { verifyTokenApi } from "../services/auth.service";
import { logout } from "../store/slices/profile.slice";
import { getTenantSetupCheckApi, updateTenantTutorialApi } from "../services/tenantSetup.service";
import { clearAuthCookies, getAuthCookieValue } from "../utils/authCookie";
import { getLoginPathForTenantContext } from "../utils/tenantFromHost";

const ProtectedLayout = (props) => {
  const { Content } = Layout;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Initialize collapsed state based on screen size
  const [collapsed, setCollapsed] = useState(() => {
    return window.innerWidth <= 768 ? true : true; // Start collapsed on mobile
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTenantSetupCompleted, setIsTenantSetupCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [tourOpen, setTourOpen] = useState(false);

  const { children } = props;
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const nextQuery = !window.location.pathname.includes("/login")
    ? `?next=${encodeURIComponent(currentPath)}`
    : "";

  useEffect(() => {
    // Initialize authentication state
    const token = getAuthCookieValue("authToken");
    setIsAuthenticated(!!token);
    if (!token) {
      setIsLoading(false);
      return;
    }

    const resolveSetupComplete = (response) => {
      if (typeof response === "boolean") return response;
      if (response && typeof response === "object") {
        if (typeof response.setup_complete === "boolean") return response.setup_complete;
        if (typeof response.is_setup_complete === "boolean") return response.is_setup_complete;
        if (typeof response.tenant_setup_completed === "boolean") return response.tenant_setup_completed;
        if (typeof response.completed === "boolean") return response.completed;
        if (response.status === "success") return true;
      }
      return false;
    };

    dispatch(
      getTenantSetupCheckApi({
        finalCallback: () => {
          setIsLoading(false);
        },
        successCallback: (response) => {
          const isSetupComplete = resolveSetupComplete(response);
          setIsTenantSetupCompleted(isSetupComplete);
          localStorage.setItem("tenantSetupCompleted", isSetupComplete ? "true" : "false");

          if (response && typeof response.tutorial_complete !== "undefined") {
            localStorage.setItem("tutorialComplete", response.tutorial_complete.toString());
          }
        },
        failureCallback: () => {
          setIsTenantSetupCompleted(false);
          localStorage.setItem("tenantSetupCompleted", "false");
        },
      })
    );
  }, [dispatch]);

  useEffect(() => {
    // Only verify token if user is authenticated
    if (isAuthenticated) {
      dispatch(
        verifyTokenApi({
          finalCallback: () => {},
          successCallback: () => {},
          failureCallback: (error) => {
            // Token is invalid, clear auth state
            clearAuthCookies();
            localStorage.removeItem("tenantSetupCompleted");
            localStorage.removeItem("tutorialComplete");
            setIsAuthenticated(false);
          },
        })
      );
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    // Auto-open the tour on initial load if tutorial not completed
    const tutorialComplete = localStorage.getItem("tutorialComplete");
    if (tutorialComplete !== "true" && isTenantSetupCompleted) {
      setTimeout(() => setTourOpen(true), 2000);
    }
  }, [isTenantSetupCompleted]);

  const tourSteps = [
    {
      title: 'Welcome to EKO!',
      description: 'Welcome to EKO, your AI-powered customer support platform. Click the logo to navigate back to the dashboard anytime.',
      target: () => document.querySelector('.tour-logo'),
    },
    {
      title: 'Your Plan Status',
      description: 'Shows your current plan status. Click to manage billing and upgrade your plan.',
      target: () => document.querySelector('.tour-trial-button'),
    },
    {
      title: 'AI Status Indicator',
      description: 'Shows if the AI is live or offline. Click to view AI settings and configurations.',
      target: () => document.querySelector('.tour-ai-status'),
    },
    {
      title: 'Dashboard',
      description: 'Track platform performance, key metrics, and overall activity in one place.',
      target: () => document.querySelector('[data-menu-id="/dashboard"]'),
    },
    {
      title: 'Channels',
      description: 'Manage customer conversations across connected channels from a single inbox.',
      target: () => document.querySelector('[data-menu-id="/channels"]'),
    },
    {
      title: 'Knowledge Base',
      description: 'Upload, organize, and maintain source documents used by your AI.',
      target: () => document.querySelector('[data-menu-id="/knowledge-base"]'),
    },
    {
      title: 'Playground',
      description: 'Test prompts and responses safely before applying changes in production.',
      target: () => document.querySelector('[data-menu-id="/playground"]'),
    },
    {
      title: 'Products',
      description: 'Create and manage product/catalog structures used by your workflows and AI context.',
      target: () => document.querySelector('[data-menu-id="/products"]'),
    },
    {
      title: 'Feedback Log',
      description: 'Review feedback on AI responses and use it to improve quality over time.',
      target: () => document.querySelector('[data-menu-id="/feedback-log"]'),
    },
    {
      title: 'Call To Action',
      description: 'Manage follow-up actions and engagement prompts shared with customers.',
      target: () => document.querySelector('[data-menu-id="/cta"]'),
    },
    {
      title: 'Setup',
      description: 'Configure onboarding and foundational settings for tenant-level behavior.',
      target: () => document.querySelector('[data-menu-id="/setup"]'),
    },
    {
      title: 'Invite User',
      description: 'Generate invite links to add teammates with the right access roles.',
      target: () => document.querySelector('[data-menu-id="/invite-user"]'),
    },
    {
      title: 'FAQ',
      description: 'Manage frequently asked questions and verified answers for faster support.',
      target: () => document.querySelector('[data-menu-id="/faq"]'),
    },
    {
      title: 'Settings',
      description: 'Manage account, agents, billing, AI behavior, and integration settings.',
      target: () => document.querySelector('[data-menu-id="/settings"]'),
    },
  ].filter((step) => {
    if (typeof step?.target !== "function") return true;
    try {
      return !!step.target();
    } catch {
      return false;
    }
  });

  // Show loading state while checking authentication and tenant setup
  if (isLoading) {
    // return <div>Loading...</div>;
    return null;
  }

  if (!isAuthenticated) {
    const slug = getAuthCookieValue("slug") || "";
    const loginPath = getLoginPathForTenantContext({ slug });
    return <Navigate to={`${loginPath}${nextQuery}`} replace />;
  }

  // Check if tenant setup is completed for authenticated users
  if (isAuthenticated && !isTenantSetupCompleted) {
    return <Navigate to="/tenant-setup" replace />;
  }

  return (
    <ConfigProvider>
      <Layout
        className="protected-layout"
        id="app-layout"
      >
        {/* <ProtectedHeader /> */}
        <Layout className="protected-layout__content">
          <SideNav collapsed={collapsed} setCollapsed={setCollapsed} />
          {/* <Layout style={{ marginLeft: collapsedWidth - 5 }}> */}
          <Layout style={{ marginLeft: "0" }}>
            <AppHeader collapsed={collapsed} setCollapsed={setCollapsed} />
            <Content
              style={{
                padding: "10px 20px",
                height: "calc(100vh - 50px)", // Account for header height (50px)
                overflow: "auto", // Allow Content wrapper to scroll
                display: "flex", // Make Content a flex container
                flexDirection: "column", // Stack children vertically
              }}
            >
              {children}
              {/* <FloatButton.Group
                trigger="hover"
                type="primary"
                style={{
                  right: 24,
                }}
                icon={<FolderOutlined />}
              >
                <FloatButton onClick={() => setTourOpen(true)} tooltip="Start Tour" size="large" />
              </FloatButton.Group> */}
            </Content>
          </Layout>
        </Layout>
      </Layout>
      <Tour
        open={tourOpen}
        onClose={() => {
          setTourOpen(false);
          // Update tutorial completion status via API
          dispatch(
            updateTenantTutorialApi({
              status: true,
              finalCallback: () => {},
              successCallback: () => {
                localStorage.setItem("tutorialComplete", "true");
              },
              failureCallback: (error) => {
                console.error("Failed to update tutorial status:", error);
                // Still save locally even if API fails
                localStorage.setItem("tutorialComplete", "true");
              },
            })
          );
        }}
        steps={tourSteps}
        popupClassName="large-tour"
      />
    </ConfigProvider>
  );
};

export default ProtectedLayout;
